import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '../types';
import BoxRejectModal from './BoxRejectModal';
import { useToast } from './shared/Toast';
import { Check, X, Clock } from 'lucide-react';
import { prepWindow, clockAfter, suggestedPreset } from '../utils/efoodPrep';
// 24ωρη ώρα χωρίς «24:15» (hour12:false)· η ζώνη μένει του browser, όπως η ημερομηνία δίπλα της.
import { fmtClock } from '../utils/athensDate';

// Import logos for delivery method display
import efoodLogo from '../assets/logos/logo_efood.png';
import woltLogo from '../assets/logos/logo_wolt.png';

type DeliveryMethod = 'self' | 'efood' | 'wolt' | 'efood_lastmile' | 'wolt_drive';

interface StoreDeliverySettings {
  is_self_delivery: boolean;
  has_efood_lastmile: boolean;
  has_wolt_drive: boolean;
}

interface InlineOrderAcceptanceProps {
  order: Order;
  presetTimes?: number[];
  onAccept: (estimatedTime: number, deliveryMethod?: DeliveryMethod) => Promise<void>;
  onReject: (reason?: string) => Promise<void>;
  /** Called when an accept fails because the order is no longer acceptable on
   *  the platform (expired/already handled). The surface should drop the stuck
   *  order — refresh the list / dequeue the popup. */
  onStale?: () => void;
  /** Persistently resolve a stuck platform order locally (cancel on our side)
   *  when the platform refuses both accept and reject. Should call the backend
   *  /dismiss endpoint and then drop the order. */
  onDismiss?: (reason: string) => Promise<void>;
  hideRejectButton?: boolean;
  isTakeaway?: boolean;
  isPreOrder?: boolean;
  preOrderTime?: Date | null;
  storeDeliverySettings?: StoreDeliverySettings;
}

const InlineOrderAcceptance: React.FC<InlineOrderAcceptanceProps> = ({
  order,
  presetTimes: customPresetTimes,
  onAccept,
  onReject,
  onStale,
  onDismiss,
  hideRejectButton = false,
  isTakeaway = false,
  isPreOrder = false,
  preOrderTime = null,
  storeDeliverySettings
}) => {
  const { t } = useTranslation('orders');
  const { showToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showBoxRejectModal, setShowBoxRejectModal] = useState(false);
  const [requestingDriver, setRequestingDriver] = useState<'efood_lastmile' | 'wolt_drive' | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [customTime, setCustomTime] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  // When the platform refuses the action and there's no way forward (expired
  // order etc.), we surface the reason + a Dismiss action instead of a dead-end
  // alert, so the blocking popup can be cleared.
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState(false);

  const presetTimes = customPresetTimes || [15, 20, 30, 45];

  // Το παράθυρο του efood είναι αγκυρωμένο στη στιγμή της παραγγελίας, όχι στο
  // «τώρα» μας — άρα ΜΙΚΡΑΙΝΕΙ όσο η παραγγελία περιμένει. Το ρολόι τικάρει
  // ώστε τα κουμπιά να κλείνουν μόνα τους αντί να στείλουν κάτι που έληξε.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Υπάρχει μόνο στις efood παραγγελίες που φέρνουν preparation_time_minutes
  // (platform_delivery)· αλλού είναι null και τα preset μένουν ως έχουν.
  const efoodWindow = order.platform_name === 'efood'
    ? prepWindow((order as any).order_json, now)
    : null;
  const isAllowed = (time: number) =>
    !efoodWindow || (time >= efoodWindow.minFromNow && time <= efoodWindow.maxFromNow);
  const suggested = suggestedPreset(presetTimes, efoodWindow);

  // Κανένα preset μέσα στο παράθυρο: στένεψε κάτω από το μικρότερο κουμπί ή
  // έληξε, επειδή η παραγγελία περίμενε. Πριν, όλα τα κουμπιά γκρίζαραν και η
  // παραγγελία δεν γινόταν δεκτή με ΚΑΝΕΝΑΝ τρόπο — ούτε με «δέξου κανονικά»
  // (κατ. 19, #829, 12/9: «επιτρέπει 3–3′», κουμπιά 5–30). Ο server κόβει τον
  // χρόνο μέσα στο παράθυρο ή, αν έληξε, δέχεται χωρίς νέα ώρα παραλαβής —
  // οπότε αυτό το κουμπί περνά πάντα. Όσο υπάρχει παράθυρο δίνουμε το μέγιστο
  // που επιτρέπει: περισσότερος χρόνος για την κουζίνα.
  const fallbackTime = efoodWindow && !presetTimes.some(isAllowed)
    ? (efoodWindow.expired ? efoodWindow.prep : efoodWindow.maxFromNow)
    : null;

  const getDefaultDeliveryMethod = (): DeliveryMethod => {
    const platform = order.platform_name;
    const settings = storeDeliverySettings;
    if (!settings) {
      if (platform === 'efood') return 'efood';
      if (platform === 'wolt') return 'wolt';
      return 'self';
    }
    if (settings.is_self_delivery) return 'self';
    if (platform === 'efood') return 'efood';
    if (platform === 'wolt') return 'wolt';
    return 'self';
  };

  const canRequestEfoodLastMile = (): boolean => {
    const settings = storeDeliverySettings;
    if (!settings) return false;
    return settings.is_self_delivery && settings.has_efood_lastmile && order.platform_name !== 'efood' && !isTakeaway;
  };

  const canRequestWoltDrive = (): boolean => {
    const settings = storeDeliverySettings;
    if (!settings) return false;
    return settings.is_self_delivery && settings.has_wolt_drive && order.platform_name !== 'wolt' && !isTakeaway;
  };

  const handleAcceptWithTime = async (time: number) => {
    if (!time || isNaN(time) || time < 1) return;
    setIsProcessing(true);
    try {
      const deliveryMethod = isTakeaway ? undefined : getDefaultDeliveryMethod();
      await onAccept(time, deliveryMethod);
      setShowControls(false);
    } catch (error: any) {
      console.error('Error accepting order:', error);
      // Surface the platform's actual reason (e.g. "expired on Wolt…") rather
      // than a generic failure. The error arrives either as a fetch-thrown
      // Error(message) or an axios error with response.data.{message,code}.
      const data = error?.response?.data;
      const msg = data?.message || error?.message;
      const status = error?.status ?? error?.response?.status;
      const dismissible = data?.dismissible || error?.dismissible;
      const stale =
        data?.code === 'ORDER_NOT_ACCEPTABLE' ||
        error?.code === 'ORDER_NOT_ACCEPTABLE' ||
        status === 409 || status === 404;
      // Platform refused and it won't recover (expired/too old) → show the reason
      // with a Dismiss action so the cashier can clear the popup, rather than a
      // dead-end alert that leaves them stuck retrying.
      if (dismissible && onDismiss) {
        setBlockedReason(msg || t('acceptFailed'));
        return;
      }
      showToast('error', msg || t('acceptFailed'));
      // No longer acceptable on the platform → let the surface drop the stuck
      // order (refresh list / dequeue popup) so the cashier isn't stuck on it.
      if (stale) onStale?.();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCustomSubmit = () => {
    const time = parseInt(customTime);
    if (isNaN(time) || time <= 0) return;
    // Με ληγμένο παράθυρο δεν υπάρχει εύρος να τηρηθεί — ο server δέχεται χωρίς νέα ώρα.
    if (efoodWindow && !efoodWindow.expired && !isAllowed(time)) {
      showToast('error', `Το efood δέχεται ${efoodWindow.minFromNow}–${efoodWindow.maxFromNow}′ για αυτή την παραγγελία.`);
      return;
    }
    handleAcceptWithTime(time);
  };

  const handleRequestExternalDriver = async (driverType: 'efood_lastmile' | 'wolt_drive', time: number) => {
    setRequestingDriver(driverType);
    setIsProcessing(true);
    try {
      await onAccept(time, driverType);
      setShowControls(false);
    } catch (error) {
      console.error(`Error requesting ${driverType} driver:`, error);
      showToast('error', t('driverRequestFailed'));
    } finally {
      setIsProcessing(false);
      setRequestingDriver(null);
    }
  };

  // Two-step reject (πρώτο πάτημα οπλίζει, δεύτερο εκτελεί) — αντικαθιστά το
  // window.confirm που μπλόκαρε την οθόνη αφής.
  const [rejectArmed, setRejectArmed] = useState(false);
  useEffect(() => {
    if (!rejectArmed) return;
    const timer = setTimeout(() => setRejectArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [rejectArmed]);

  const handleReject = async () => {
    // BOX and efood require a reason from a fixed list — open the reason picker.
    // (The backend maps the chosen code to each platform's accepted value.)
    if (order.platform_name === 'box' || order.platform_name === 'efood') {
      setShowBoxRejectModal(true);
      return;
    }
    if (!rejectArmed) { setRejectArmed(true); return; }
    setRejectArmed(false);
    setIsProcessing(true);
    try {
      await onReject();
      setShowControls(false);
    } catch (error: any) {
      console.error('Error rejecting order:', error);
      const msg = error?.response?.data?.message || error?.message;
      // If reject also fails on the platform, the only way out is a local dismiss.
      if (onDismiss) setBlockedReason(msg || t('rejectFailed'));
      else showToast('error', msg || t('rejectFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBoxReject = async (reason: string) => {
    setIsProcessing(true);
    setShowBoxRejectModal(false);
    try {
      await onReject(reason);
      setShowControls(false);
    } catch (error: any) {
      console.error('Error rejecting BOX order:', error);
      const msg = error?.response?.data?.message || error?.message;
      if (onDismiss) setBlockedReason(msg || t('rejectFailed'));
      else showToast('error', msg || t('rejectFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Local dismiss — resolve the stuck order on our side (calls backend /dismiss).
  const handleDismiss = async () => {
    if (!onDismiss) return;
    setDismissing(true);
    try {
      await onDismiss(blockedReason || 'Απορρίφθηκε από την πλατφόρμα');
      setShowControls(false);
    } catch (error: any) {
      showToast('error', error?.response?.data?.message || error?.message || t('dismissFailed', { defaultValue: 'Αποτυχία αφαίρεσης' }));
    } finally {
      setDismissing(false);
    }
  };

  // 'pending_accept' = auto-accept failed and the scheduler retries — but the
  // cashier may also accept manually right now (Box's "already accepted" 422 is
  // reconciled server-side), instead of staring at a stuck order with no button.
  if (!showControls || !['pending', 'pending_accept'].includes(order.status)) return null;

  // Platform refused the action (expired/too old) — show the reason + a way out
  // so the blocking popup can be cleared instead of getting stuck.
  if (blockedReason) {
    return (
      <div className="mt-3 rounded-xl border border-red-300 dark:border-red-700/60 bg-red-100 dark:bg-red-950/40 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <X className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-red-700 dark:text-red-300">{t('platformRefused', { defaultValue: 'Η πλατφόρμα απέρριψε την ενέργεια' })}</p>
            <p className="text-xs text-red-800/90 dark:text-red-200/90 mt-1 break-words">{blockedReason}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setBlockedReason(null)}
            disabled={dismissing}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-surface-2 hover:bg-surface-3 text-t1 disabled:opacity-50"
          >
            {t('tryAgain', { defaultValue: 'Δοκιμή ξανά' })}
          </button>
          {onDismiss && (
            <button
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {dismissing ? '…' : t('dismissOrder', { defaultValue: 'Αφαίρεση από τη λίστα' })}
            </button>
          )}
        </div>
        <p className="text-[11px] text-red-800/60 dark:text-red-200/60 leading-snug">
          {t('dismissHint', { defaultValue: 'Η αφαίρεση κλείνει την παραγγελία μόνο στο δικό μας σύστημα (η πλατφόρμα την έχει ήδη λήξει/ακυρώσει).' })}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={`mt-3 rounded-xl border ${
        isPreOrder
          ? 'bg-red-100 dark:bg-red-950/40 border-red-300 dark:border-red-700/60'
          : 'bg-surface-2 dark:bg-surface-2/50 border-line-strong'
      }`}>

        {/* Pre-order Warning */}
        {isPreOrder && preOrderTime && (
          <div className="px-4 pt-3">
            <div className="flex items-center gap-2 bg-red-600/90 text-white px-3 py-2 rounded-lg text-sm font-semibold">
              <Clock className="w-4 h-4 shrink-0" />
              {t('preOrderFor', {
                date: preOrderTime.toLocaleDateString(),
                time: fmtClock(preOrderTime, { zone: 'local' })
              })}
            </div>
          </div>
        )}

        <div className="p-3 space-y-2.5">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full shrink-0 ${
                isPreOrder ? 'bg-red-400' : 'bg-amber-400 animate-pulse'
              }`} />
              <span className="text-sm font-semibold text-t1">
                {isPreOrder
                  ? t('acceptPreOrder')
                  : isTakeaway ? t('quickAcceptPickup') : t('quickAcceptDelivery')
                }
              </span>
            </div>

            {!hideRejectButton && (
              <button
                onClick={handleReject}
                disabled={isProcessing}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${rejectArmed ? 'bg-red-600 text-white border-red-500 animate-pulse' : 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700/50 hover:bg-red-200 dark:hover:bg-red-900/60 hover:text-red-700 dark:hover:text-red-300'}`}
              >
                <X className="w-3 h-3" />
                {rejectArmed ? 'Σίγουρα; Πάτησε ξανά' : t('reject')}
              </button>
            )}
          </div>

          {/* efood: τι προτείνει η πλατφόρμα και μέχρι πότε μπορούμε να πάμε */}
          {efoodWindow && (
            <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] border ${
              efoodWindow.expired
                ? 'bg-red-100 dark:bg-red-950/40 border-red-300 dark:border-red-800/60 text-red-700 dark:text-red-300'
                : 'bg-surface-0/50 border-line-strong/60 text-t3'
            }`}>
              <img src={efoodLogo} alt="e-food" className="w-3.5 h-3.5 object-contain shrink-0" />
              {efoodWindow.expired ? (
                <span>Το περιθώριο του efood έχει περάσει — δέξου την παραγγελία κανονικά, απλώς δεν δηλώνεται νέα ώρα παραλαβής.</span>
              ) : (
                <span>
                  Προτείνει <strong className="text-t2">{efoodWindow.prep}′</strong>
                  {' · '}επιτρέπει <strong className="text-t2">{efoodWindow.minFromNow}–{efoodWindow.maxFromNow}′</strong>
                  {' · '}παραλαβή έως {efoodWindow.latest.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}

          {/* Κανένα κουμπί δεν χωράει στο παράθυρο του efood — ένα που περνά πάντα */}
          {efoodWindow && fallbackTime !== null && (
            <button
              onClick={() => handleAcceptWithTime(fallbackTime)}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-500 border border-green-500 shadow-sm shadow-green-900/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              {efoodWindow.expired
                ? 'Αποδοχή τώρα'
                : `Αποδοχή — έτοιμο σε ${fallbackTime}′ (${clockAfter(fallbackTime, now)})`}
            </button>
          )}

          {/* Preset time buttons */}
          <div className="grid grid-cols-4 gap-1.5">
            {presetTimes.map((time) => {
              const blocked = !isAllowed(time);
              return (
              <button
                key={time}
                onClick={() => handleAcceptWithTime(time)}
                disabled={isProcessing || blocked}
                title={blocked ? 'Εκτός του επιτρεπτού παραθύρου του efood' : undefined}
                className={`relative flex flex-col items-center py-2.5 rounded-lg font-semibold transition-all border ${
                  isProcessing
                    ? 'bg-surface-2 border-line-strong text-t4 cursor-not-allowed'
                    : blocked
                      ? 'bg-surface-1/60 border-line text-t4 cursor-not-allowed line-through decoration-slate-600'
                      : selectedTime === time
                        ? 'bg-green-600 border-green-500 text-white shadow-sm shadow-green-900/40'
                        : suggested === time
                          ? 'bg-surface-2 border-amber-500/60 text-t1 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300'
                          : 'bg-surface-2 border-line-strong text-t2 hover:border-green-500/60 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300'
                }`}
              >
                {isProcessing ? (
                  <svg className="animate-spin h-4 w-4 text-t3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <>
                    {suggested === time && !blocked && (
                      <span className="absolute -top-1.5 px-1 rounded bg-amber-500 text-[9px] font-bold text-slate-900 leading-tight">efood</span>
                    )}
                    <span className="text-base font-bold leading-none">{time}</span>
                    {/* Ώρα ρολογιού: εκεί δεν χωράει παρεξήγηση για το «σε πόσο» */}
                    <span className="text-[10px] text-t3 mt-0.5">
                      {efoodWindow && !blocked ? clockAfter(time, now) : 'min'}
                    </span>
                  </>
                )}
              </button>
              );
            })}
          </div>

          {/* Custom time */}
          {showCustomInput ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                placeholder={efoodWindow && !efoodWindow.expired ? `${efoodWindow.minFromNow}–${efoodWindow.maxFromNow}` : 'π.χ. 50'}
                min={efoodWindow && !efoodWindow.expired ? efoodWindow.minFromNow : 1}
                max={efoodWindow && !efoodWindow.expired ? efoodWindow.maxFromNow : 180}
                autoFocus
                disabled={isProcessing}
                className="flex-1 px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:opacity-40"
              />
              <span className="text-xs text-t3 shrink-0">min</span>
              <button
                onClick={handleCustomSubmit}
                disabled={isProcessing || !customTime || isNaN(parseInt(customTime)) || parseInt(customTime) < 1}
                className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setShowCustomInput(false); setCustomTime(''); }}
                disabled={isProcessing}
                className="px-3 py-2 bg-surface-2 hover:bg-surface-3 text-t3 rounded-lg text-sm transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCustomInput(true)}
              disabled={isProcessing}
              className="w-full text-xs text-t3 hover:text-t2 py-1 transition-colors"
            >
              + {t('enterCustomTime')}
            </button>
          )}

          {/* External Driver Request Buttons */}
          {(canRequestEfoodLastMile() || canRequestWoltDrive()) && (
            <div className="pt-2 border-t border-line-strong">
              <p className="text-[11px] text-t4 mb-2 text-center">{t('requestExternalDriver')}</p>
              <div className="flex gap-2">
                {canRequestEfoodLastMile() && (
                  <button
                    onClick={() => {
                      const timeVal = parseInt(customTime) || presetTimes[1] || 30;
                      handleRequestExternalDriver('efood_lastmile', timeVal);
                    }}
                    disabled={isProcessing}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium ${
                      requestingDriver === 'efood_lastmile'
                        ? 'border-red-500 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        : 'border-line-strong bg-surface-2 text-t2 hover:border-red-600/60 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <img src={efoodLogo} alt="e-food" className="w-4 h-4 object-contain" />
                    <span>{requestingDriver === 'efood_lastmile' ? t('requesting') : t('callEfoodDriver')}</span>
                  </button>
                )}

                {canRequestWoltDrive() && (
                  <button
                    onClick={() => {
                      const timeVal = parseInt(customTime) || presetTimes[1] || 30;
                      handleRequestExternalDriver('wolt_drive', timeVal);
                    }}
                    disabled={isProcessing}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium ${
                      requestingDriver === 'wolt_drive'
                        ? 'border-cyan-500 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
                        : 'border-line-strong bg-surface-2 text-t2 hover:border-cyan-600/60 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 hover:text-cyan-700 dark:hover:text-cyan-300'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <img src={woltLogo} alt="Wolt" className="w-4 h-4 object-contain" />
                    <span>{requestingDriver === 'wolt_drive' ? t('requesting') : t('callWoltDriver')}</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <BoxRejectModal
        isOpen={showBoxRejectModal}
        onClose={() => setShowBoxRejectModal(false)}
        onReject={handleBoxReject}
        orderNumber={order.short_code || order.platform_order_id || ''}
      />
    </>
  );
};

export default InlineOrderAcceptance;
